// React
import { useState, Fragment } from "react"

// Translations
import { useTrans } from "@/i18n"

// Assets
import BrandLogo from "@/assets/icons/brand/zonhub_logo.svg?react"

// UI
import { cn } from "@/lib/utils"
import { LanguageSelector } from "@/components/LanguageSelector"
import { Button } from "@/components/Button"
import { Dialog, DialogBackdrop, Transition, TransitionChild } from "@/lib/ui"

// Icons
import { MenuIcon } from "@/components/MenuIcon"

// Environment
import { API_URL, AUTH_LOGIN_URL } from "@/lib/env"

// API
import { useApiCmsNavigationList } from "@/api/rest/generated/api/api"
import { NavigationItemType } from "@/api/rest/generated/@types/navigationItemType"

/**
 * HeaderNoAuth
 * @returns
 */
export const HeaderNoAuth = () => {
	const t = useTrans("common")

	// Query
	const { data: navigation } = useApiCmsNavigationList()

	// State for menu toggles
	const [navbarOpen, setNavbarOpen] = useState(false)

	// Template
	return (
		<>
			<div className="inset-x-0 top-0 z-50 mx-auto max-w-7xl bg-white px-4 sm:px-6 lg:px-8">
				<div className="mx-auto">
					<div className="z-50 flex w-full items-center justify-between">
						{/* Logo */}
						<a
							href={API_URL}
							className="block h-full py-3"
							aria-label={t("common.web.homepage")}
						>
							<BrandLogo />
						</a>

						{/* Desktop navigation - visible on lg+ screens */}
						<nav className="hidden lg:block">
							<ul className="flex">
								{navigation &&
									navigation.map((item) => {
										if (item.has_submenu) {
											return (
												<DesktopNavItemWithSubMenu
													key={item.title}
													item={item}
												/>
											)
										} else {
											return (
												<li key={item.title}>
													<a
														href={item.url}
														className="hover:text-secondary flex py-2 text-base font-normal lg:ml-12 lg:inline-flex"
														target={
															item.open_in_new_tab
																? "_blank"
																: undefined
														}
														rel={
															item.open_in_new_tab
																? "noreferrer"
																: undefined
														}
													>
														{item.title}
													</a>
												</li>
											)
										}
									})}
							</ul>
						</nav>

						<div className="flex items-center gap-3">
							{/* Login / Signup */}
							<div className="hidden justify-end gap-3 pr-16 sm:flex lg:pr-0">
								<Button
									variant="secondary"
									onClick={() => {
										window.location.href = AUTH_LOGIN_URL
									}}
								>
									{t("common:common.nav.dashboard")}
								</Button>

								<LanguageSelector />
							</div>

							{/* Mobile menu button */}
							<button
								type="button"
								className="w-14 px-3 lg:hidden"
								onClick={() => setNavbarOpen(!navbarOpen)}
								data-testid="navigation.open_sidebar"
							>
								<span className="sr-only">Open menu</span>
								<MenuIcon
									isOpen={true}
									aria-hidden="true"
									className="h-3 w-6"
								/>
							</button>
						</div>
					</div>
				</div>
			</div>

			{/* Mobile slide-in navigation */}
			<Transition show={navbarOpen} as={Fragment}>
				<Dialog
					as="div"
					className="lg:hidden"
					onClose={() => setNavbarOpen(false)}
				>
					<div className="fixed inset-0 z-40 flex h-full">
						<TransitionChild as={Fragment}>
							<DialogBackdrop
								className={cn(
									"fixed inset-0 bg-gray-600 bg-opacity-75",
									"transition duration-300 ease-in-out data-[closed]:opacity-0",
								)}
							/>
						</TransitionChild>
						<TransitionChild as={Fragment}>
							<div
								className={cn(
									"relative flex w-full max-w-[256px] flex-1 flex-col bg-white",
									"transition duration-300 ease-in-out data-[closed]:-translate-x-full sm:max-w-sm",
								)}
							>
								<TransitionChild as={Fragment}>
									<div
										className={cn(
											"absolute right-0 top-1 -mr-14 p-1 transition duration-300 ease-in-out data-[closed]:opacity-0",
											"data-[closed]:-translate-x-full",
										)}
									>
										<button
											type="button"
											className="flex size-12 items-center justify-center"
											onClick={() => setNavbarOpen(false)}
										>
											<MenuIcon
												className="size-6"
												aria-hidden="true"
												isOpen={false}
											/>
											<span className="sr-only">
												Close menu
											</span>
										</button>
									</div>
								</TransitionChild>
								<div className="flex h-screen flex-1 flex-col overflow-y-auto">
									<div
										className={cn(
											"flex h-14 shrink-0 items-center bg-primary-500 shadow-[0px_1px_3px_rgba(0,0,0,0.25)] 2xl:h-16",
											"pl-2",
										)}
									>
										<a
											href={API_URL}
											className="block h-full py-3"
											aria-label={t(
												"common.web.homepage",
											)}
										>
											<BrandLogo />
										</a>
									</div>
									{/* Navigation items */}
									<div className="flex-1">
										<div className="space-y-1 p-4">
											{navigation &&
												navigation.map((item) => {
													if (item.has_submenu) {
														return (
															<MobileNavItemWithSubMenu
																key={item.title}
																item={item}
																onItemClick={() =>
																	setNavbarOpen(
																		false,
																	)
																}
															/>
														)
													} else {
														return (
															<a
																key={item.title}
																href={item.url}
																className="block rounded-md px-3 py-2 text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
																target={
																	item.open_in_new_tab
																		? "_blank"
																		: undefined
																}
																rel={
																	item.open_in_new_tab
																		? "noreferrer"
																		: undefined
																}
																onClick={() =>
																	setNavbarOpen(
																		false,
																	)
																}
															>
																{item.title}
															</a>
														)
													}
												})}
										</div>
									</div>

									{/* Footer with Login button */}
									<div className="shrink-0 space-y-3 border-t border-gray-200 py-4">
										<div className="space-y-3 px-4">
											<Button
												variant="secondary"
												className="w-full"
												onClick={() => {
													window.location.href =
														AUTH_LOGIN_URL
												}}
											>
												{t(
													"common:common.nav.dashboard",
												)}
											</Button>
											<LanguageSelector />
										</div>
									</div>
								</div>
							</div>
						</TransitionChild>
						<div className="w-14 shrink-0" aria-hidden="true">
							{/* Dummy element to force sidebar to shrink to fit close icon */}
						</div>
					</div>
				</Dialog>
			</Transition>
		</>
	)
}

/**
 * DesktopNavItemWithSubMenu - Original desktop dropdown style
 * @param param0
 * @returns
 */
function DesktopNavItemWithSubMenu({ item }: { item: NavigationItemType }) {
	const [showSubMenu, setShowSubMenu] = useState(false)

	return (
		<li className="relative">
			<button
				className="hover:text-secondary flex w-full items-center justify-between gap-2 py-2 text-base font-normal lg:ml-12 lg:inline-flex lg:w-auto lg:justify-center"
				onClick={() => setShowSubMenu(!showSubMenu)}
				onBlur={() => setShowSubMenu(false)}
				aria-expanded={showSubMenu}
			>
				{item.title}
				{/* Collapse / Expand Arrow */}
				<span
					className={`duration-200${showSubMenu ? "-scale-y-100" : ""}`}
				>
					<svg
						width="20"
						height="20"
						viewBox="0 0 20 20"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path /* eslint-disable-next-line max-len */
							d="M10 14.25C9.8125 14.25 9.65625 14.1875 9.5 14.0625L2.3125 7C2.03125 6.71875 2.03125 6.28125 2.3125 6C2.59375 5.71875 3.03125 5.71875 3.3125 6L10 12.5312L16.6875 5.9375C16.9688 5.65625 17.4062 5.65625 17.6875 5.9375C17.9688 6.21875 17.9688 6.65625 17.6875 6.9375L10.5 14C10.3437 14.1562 10.1875 14.25 10 14.25Z"
							fill="currentColor"
						/>
					</svg>
				</span>
			</button>
			<div
				className={cn(
					"z-50 w-full rounded-xl bg-white p-2 shadow-lg lg:absolute lg:left-0 lg:top-full lg:m-8 lg:w-[630px] lg:p-8",
					showSubMenu ? "block" : "hidden",
				)}
			>
				<div className="grid gap-5 lg:auto-cols-max lg:grid-flow-col lg:justify-between">
					{item.sub_menu_headers.map((header) => (
						<div key={header.title}>
							<h4 className="mb-3 text-sm font-bold">
								{header.title}
							</h4>
							<div className="flex flex-col space-y-3">
								{header.sub_menu_items.map((subitem) => (
									<a
										key={subitem.title}
										href={subitem.url}
										className="text-body-color hover:text-secondary text-base font-normal"
										target={
											subitem.open_in_new_tab
												? "_blank"
												: undefined
										}
										rel={
											subitem.open_in_new_tab
												? "noreferrer"
												: undefined
										}
									>
										{subitem.title}
									</a>
								))}
							</div>
						</div>
					))}
				</div>
			</div>
		</li>
	)
}

/**
 * MobileNavItemWithSubMenu - Mobile slide-in panel style
 * @param param0
 * @returns
 */
function MobileNavItemWithSubMenu({
	item,
	onItemClick,
}: {
	item: NavigationItemType
	onItemClick: () => void
}) {
	const [showSubMenu, setShowSubMenu] = useState(false)

	return (
		<div className="space-y-1">
			<button
				className="flex w-full items-center justify-between rounded-md px-3 py-2 text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900"
				onClick={() => setShowSubMenu(!showSubMenu)}
				aria-expanded={showSubMenu}
			>
				{item.title}
				<svg
					className={cn(
						"ml-2 size-5 transition-transform duration-200",
						showSubMenu ? "rotate-180" : "",
					)}
					fill="none"
					viewBox="0 0 20 20"
				>
					<path
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="1.5"
						d="M6 8l4 4 4-4"
					/>
				</svg>
			</button>
			{showSubMenu && (
				<div className="space-y-2 pl-4">
					{item.sub_menu_headers.map((header) => (
						<div key={header.title} className="space-y-2">
							<div className="space-y-1">
								{header.sub_menu_items.map((subitem) => (
									<a
										key={subitem.title}
										href={subitem.url}
										className="block rounded-md px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900"
										target={
											subitem.open_in_new_tab
												? "_blank"
												: undefined
										}
										rel={
											subitem.open_in_new_tab
												? "noreferrer"
												: undefined
										}
										onClick={onItemClick}
									>
										{subitem.title}
									</a>
								))}
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	)
}
